import { useState, useMemo } from "react";
import {
  Search,
  Download,
  RotateCcw,
  Plus,
  BarChart3,
  Filter,
} from "lucide-react";
import { Button, KpiGrid, Pagination } from "@/shared/ui";
import { SearchableSelect } from "@/shared/ui/core/SearchableSelect";
import { cn } from "@/shared/lib/cn";
import {
  MOCK_TABLE_ROWS,
  STATUS_FILTER_OPTIONS,
  PLANT_FILTER_OPTIONS,
  type TestUiListItem,
} from "../model/testUiData";
import { TestUiListTable } from "./TestUiListTable";

export interface TestUiListSectionProps {
  isMobilePreview: boolean;
  onSelectRow: (item: TestUiListItem) => void;
  onOpenCreate: () => void;
}

export function TestUiListSection({
  isMobilePreview,
  onSelectRow,
  onOpenCreate,
}: TestUiListSectionProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [plantFilter, setPlantFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const [isPageLoading, setIsPageLoading] = useState(false);

  // Toggle states for collapsible UI
  const [showInsights, setShowInsights] = useState(true);
  const [showFilters, setShowFilters] = useState(true);

  // Active filters calculation
  const activeFilterCount =
    (searchQuery.trim() ? 1 : 0) +
    (statusFilter !== "all" ? 1 : 0) +
    (plantFilter !== "all" ? 1 : 0);

  // Filter logic
  const filteredRows = useMemo(() => {
    return MOCK_TABLE_ROWS.filter((r) => {
      const matchSearch =
        searchQuery === "" ||
        r.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.partnerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.lotNo.toLowerCase().includes(searchQuery.toLowerCase());
      const matchStatus = statusFilter === "all" || r.status === statusFilter;
      const matchPlant =
        plantFilter === "all" ||
        r.plant.toLowerCase().includes(plantFilter.toLowerCase());
      return matchSearch && matchStatus && matchPlant;
    });
  }, [searchQuery, statusFilter, plantFilter]);

  const handlePageChange = (newPage: number) => {
    setIsPageLoading(true);
    setCurrentPage(newPage);
    setTimeout(() => {
      setIsPageLoading(false);
    }, 350);
  };

  const handlePageSizeChange = (newSize: number) => {
    setIsPageLoading(true);
    setPageSize(newSize);
    setCurrentPage(1);
    setTimeout(() => {
      setIsPageLoading(false);
    }, 350);
  };

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  const rowNumberStart = (currentPage - 1) * pageSize + 1;

  const toggleSelectAll = () => {
    if (selectedIds.length === paginatedRows.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(paginatedRows.map((r) => r.id));
    }
  };

  const toggleSelectRow = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setPlantFilter("all");
    setCurrentPage(1);
  };

  return (
    <section className="space-y-4">
      {/* 1. Header Toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="m-0 text-h2 font-bold text-text">
            Chuẩn Màn Hình Danh Sách Master (Server List Pattern)
          </h2>
          <p className="m-0 text-caption text-text-muted">
            Header bảng chuẩn màu xanh Forest (table-head-primary) đồng bộ toàn hệ thống
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Nút bật/tắt Tóm tắt KPI */}
          <Button
            size="sm"
            variant={showInsights ? "soft" : "secondary"}
            onClick={() => setShowInsights((prev) => !prev)}
            leftIcon={<BarChart3 className="size-3.5" />}
            className="gap-1.5"
            aria-pressed={showInsights}
            aria-label={showInsights ? "Ẩn tóm tắt KPI" : "Hiện tóm tắt KPI"}
            title={showInsights ? "Ẩn tóm tắt KPI" : "Hiện tóm tắt KPI"}
          >
            <span>Tóm tắt</span>
            <span
              className={cn(
                "inline-flex min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold tabular-nums",
                showInsights ? "bg-primary text-on-primary" : "bg-bg text-text-muted border border-border"
              )}
            >
              4
            </span>
          </Button>

          {/* Nút bật/tắt Bộ lọc */}
          <Button
            size="sm"
            variant={showFilters ? "soft" : "secondary"}
            onClick={() => setShowFilters((prev) => !prev)}
            leftIcon={<Filter className="size-3.5" />}
            className="gap-1.5"
            aria-pressed={showFilters}
            aria-label={showFilters ? "Ẩn bộ lọc" : "Hiện bộ lọc"}
            title={showFilters ? "Ẩn bộ lọc" : "Hiện bộ lọc"}
          >
            <span>Bộ lọc</span>
            {activeFilterCount > 0 ? (
              <span
                className={cn(
                  "inline-flex min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold tabular-nums",
                  showFilters ? "bg-primary text-on-primary" : "bg-primary text-on-primary"
                )}
              >
                {activeFilterCount}
              </span>
            ) : null}
          </Button>

          <Button
            size="sm"
            variant="secondary"
            leftIcon={<Download className="size-3.5" />}
          >
            Xuất Excel
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={onOpenCreate}
            leftIcon={<Plus className="size-3.5" />}
          >
            Tạo lệnh mới
          </Button>
        </div>
      </div>

      {/* 2. Tóm tắt KPI (Bật/tắt) */}
      {showInsights && (
        <div className="space-y-3 animate-in fade-in duration-150">
          <KpiGrid
            items={[
              { id: "total", label: "Tổng số phiếu", badge: "Tháng 09", value: "158", hint: "+14% so với tuần trước", tone: "neutral" },
              { id: "in_prod", label: "Đang sản xuất / Gom", value: "34", hint: "Đúng tiến độ 92%", tone: "primary" },
              { id: "hold", label: "Tạm giữ QA / KCS", value: "5", hint: "Cần thẩm định CCP", tone: "warning" },
              { id: "done", label: "Hoàn tất đóng thùng", value: "119", hint: "Đã nhập kho lạnh", tone: "success" },
            ]}
          />
        </div>
      )}

      {/* 3. Thanh Bộ lọc (Bật/tắt) */}
      {showFilters ? (
        <div className="rounded-control border border-border bg-surface p-3 shadow-xs space-y-3 animate-in fade-in duration-150">
          <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
            {/* Quick Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-text-muted pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Tìm kiếm mã phiếu, đối tác, tên hàng, số lô HACCP... (/)"
                className="w-full h-[36px] min-h-[36px] rounded-md border border-border bg-bg pl-6 pr-3 text-[12px] text-text placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            {/* Searchable Select Filters */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="w-44">
                <SearchableSelect
                  size="sm"
                  options={STATUS_FILTER_OPTIONS}
                  value={statusFilter}
                  onValueChange={(val) => setStatusFilter(val)}
                  placeholder="Chọn trạng thái..."
                  searchPlaceholder="Lọc trạng thái..."
                  className="h-[36px] min-h-[36px] text-[12px] rounded-md"
                />
              </div>

              <div className="w-48">
                <SearchableSelect
                  size="sm"
                  options={PLANT_FILTER_OPTIONS}
                  value={plantFilter}
                  onValueChange={(val) => setPlantFilter(val)}
                  placeholder="Chọn phân xưởng..."
                  searchPlaceholder="Lọc phân xưởng..."
                  className="h-[36px] min-h-[36px] text-[12px] rounded-md"
                />
              </div>

              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="inline-flex items-center gap-1 h-[36px] px-2.5 text-caption font-medium rounded-md text-text-muted hover:text-text hover:bg-bg transition-colors cursor-pointer"
                >
                  <RotateCcw className="size-3.5" />
                  <span>Xóa lọc</span>
                </button>
              )}
            </div>
          </div>
        </div>
      ) : activeFilterCount > 0 ? (
        /* Gợi ý khi đóng bộ lọc nhưng đang có điều kiện lọc active */
        <div className="flex items-center justify-between rounded-md bg-surface border border-border px-3 py-2 text-caption text-text-muted animate-in fade-in duration-150">
          <div className="flex items-center gap-2">
            <span className="inline-block size-2 rounded-full bg-primary" />
            <span>
              Đang áp dụng <strong className="text-text font-semibold">{activeFilterCount}</strong> bộ lọc
              {" · "}
              <button
                type="button"
                onClick={() => setShowFilters(true)}
                className="text-primary hover:underline font-semibold cursor-pointer"
              >
                bấm Bộ lọc để chỉnh
              </button>
            </span>
          </div>
          <button
            type="button"
            onClick={handleClearFilters}
            className="inline-flex items-center gap-1 text-caption text-text-muted hover:text-text font-medium cursor-pointer"
          >
            <RotateCcw className="size-3" />
            <span>Xóa lọc</span>
          </button>
        </div>
      ) : null}

      {/* 4. Table view & mobile card stack */}
      <TestUiListTable
        isMobilePreview={isMobilePreview}
        filteredRows={paginatedRows}
        selectedIds={selectedIds}
        loading={isPageLoading}
        rowNumberStart={rowNumberStart}
        onToggleSelectAll={toggleSelectAll}
        onToggleSelectRow={toggleSelectRow}
        onSelectRow={onSelectRow}
        onOpenCreate={onOpenCreate}
        onClearSelection={() => setSelectedIds([])}
      />

      {/* 5. Core Standard Pagination Bar */}
      {filteredRows.length > 5 && (
        <div className="rounded-control border border-border bg-surface px-4 py-2.5 shadow-xs">
          <Pagination
            page={currentPage}
            pageSize={pageSize}
            total={filteredRows.length}
            loading={isPageLoading}
            onPageChange={handlePageChange}
            pageSizeOptions={[5, 10, 20]}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}
    </section>
  );
}
